import { correlationId } from '@interlucent/ids';
import { send } from '../ws';
import { waitForAck } from './waitForAck';

const DEFAULT_TIMEOUT_MS = 15_000;

export interface CreatePreviewShareOptions {
  projectId: string;
  appId: string;
  versionId: string;
  expiresInSeconds?: number;
  expiresInMinutes?: number;
  passcode?: string;
  /** pixel-stream attribute overrides (pointerLock, nativeTouch, resolutionClamp, etc.) */
  streamConfig?: Record<string, unknown>;
  /** Timeout in milliseconds (default: 15000) */
  timeoutMs?: number;
  /** External abort signal (e.g. component unmount) */
  signal?: AbortSignal;
}

/**
 * Create a preview share via WebSocket and wait for acknowledgment.
 * Uses AbortController for proper timeout and cleanup of the WS listener.
 */
export async function createPreviewShare(
  options: CreatePreviewShareOptions
): Promise<{ id: string }> {
  const {
    projectId,
    appId,
    versionId,
    expiresInSeconds,
    expiresInMinutes,
    passcode,
    streamConfig,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal: externalSignal,
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Forward external abort to internal controller
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('Aborted', 'AbortError');
    }
    externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }

  try {
    const corr = correlationId();
    send('preview.share.create', {
      projectId,
      appId,
      versionId,
      ...(expiresInSeconds !== undefined && { expiresInSeconds }),
      ...(expiresInMinutes !== undefined && { expiresInMinutes }),
      ...(passcode !== undefined && { passcode }),
      ...(streamConfig !== undefined && { streamConfig }),
    }, corr);

    const result = await waitForAck<{ id: string }>(corr, {
      signal: controller.signal,
    });
    return result;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
}
